// src/services/API.js
import { fetchJson } from "./apiClient";

const CATEGORY_PATH_PREFIX = (
  process.env.REACT_APP_API_CATEGORY_PREFIX || "/categories"
).replace(/\/+$/, "");

// Small helper to unwrap Lambda proxy { body: string } or pass through JSON
function normalizeJson(res, fallback = {}) {
  // Already a plain object with no 'body' wrapper
  if (res && typeof res === "object" && typeof res.body !== "string")
    return res;

  // Proxy form: { statusCode, headers, body: "json" }
  if (res && typeof res === "object" && typeof res.body === "string") {
    try {
      return JSON.parse(res.body);
    } catch {
      return fallback;
    }
  }
  return fallback;
}

// -------------------- Search --------------------
export async function search(q = "", opts = {}) {
  try {
    let res = await fetchJson("/search", {
      method: "get",
      params: { q, ...opts },
    });

    res = normalizeJson(res, { results: [], total: 0, q });

    // Normalize shapes
    if (Array.isArray(res)) {
      return { results: res, total: res.length, q };
    }
    if (res && typeof res === "object" && Array.isArray(res.results)) {
      return {
        results: res.results,
        total: res.total ?? res.results.length,
        q: res.q ?? q,
      };
    }
    return { results: [], total: 0, q };
  } catch {
    return { results: [], total: 0, q };
  }
}

// -------------------- Item --------------------
export async function getItem(id) {
  try {
    return await fetchJson(`/items/${id}`, { method: "get" });
  } catch (e) {
    return Promise.resolve({ id, name: `Item ${id}`, price: 0 });
  }
}

// -------------------- Category (cursor pagination) --------------------
// New signature: listByCategory(slug, { limit, order, cursor }?)
// Back-compat: listByCategory(slug, pageNumber)
export async function listByCategory(slug, opts = 1) {
  try {
    let params = {};
    if (typeof opts === "number") {
      // Backward compatible: page param (ignored by backend now)
      params = { page: opts };
    } else if (opts && typeof opts === "object") {
      const { limit, order, cursor } = opts;
      if (Number.isFinite(limit)) params.limit = limit;
      if (typeof order === "string") params.order = order;
      if (cursor) params.cursor = cursor; // base64 from API
    }

    const path = `${CATEGORY_PATH_PREFIX}/${encodeURIComponent(slug)}`;
    const res = await fetchJson(path, { method: "get", params });

    const json = normalizeJson(res, { items: [] });
    const items = Array.isArray(json.items) ? json.items : [];

    // Support both old {cursor} and new {nextCursor, hasMore}
    const nextCursor = json.nextCursor ?? json.cursor ?? null;
    const hasMore = Boolean(json.hasMore ?? nextCursor != null);

    return { items, nextCursor, hasMore };
  } catch (e) {
    return Promise.resolve({ items: [], nextCursor: null, hasMore: false });
  }
}

// -------------------- OEMN (kept as page-numbered; adjust if you migrate it) --------------------
export async function listByOEMN(slug, page = 1) {
  try {
    return await fetchJson(`/oemn/${slug}`, {
      method: "get",
      params: { page },
    });
  } catch (e) {
    return Promise.resolve({ items: [], page, total: 0 });
  }
}

// -------------------- Payments --------------------
export async function createPaymentIntent(payload = {}) {
  try {
    return await fetchJson("/payment-intents", {
      method: "post",
      data: payload,
    });
  } catch (e) {
    return Promise.resolve({ clientSecret: "test_client_secret" });
  }
}

// -------------------- Reorder (lookup + whatever server does) --------------------
export async function reorder(id) {
  try {
    let res = await fetchJson("/reorder", {
      method: "get",
      params: { id }, // => ?id=...
    });

    // Unwrap Lambda proxy: { statusCode, headers, body: "..." }
    res = normalizeJson(res, {
      orderId: id,
      items: [],
      surchargeUsd: 0,
      currency: "usd",
      status: "not_found",
    });

    const items = Array.isArray(res.items) ? res.items : [];
    return {
      orderId: res.orderId || id,
      items,
      surchargeUsd: Number(res.surchargeUsd || 0),
      currency: res.currency || "usd",
      status: res.status || "pending",
    };
  } catch (e) {
    console.error("reorder() error:", e);
    return {
      orderId: id,
      items: [],
      surchargeUsd: 0,
      currency: "usd",
      status: "error",
    };
  }
}

// -------------------- Contact --------------------
export async function sendContact(payload = {}) {
  try {
    const res = await fetchJson("/contact?ts=1134", {
      method: "post",
      data: payload,
    });

    // Unwrap Lambda proxy { body: "json" } if needed
    const json = normalizeJson(res, { ok: true });

    // Normalize to a simple shape the UI can rely on
    if (json && typeof json === "object") {
      return {
        ok: json.ok !== false, // default true unless explicitly false
        message: json.message || "",
      };
    }

    return { ok: false, message: "Unexpected response from contact API." };
  } catch (e) {
    console.error("sendContact() error:", e);
    return { ok: false, message: "Failed to send contact message." };
  }
}

// POST /api/availability/batch
// Request: { items: [{ ID, ITEM_NUMBER, qty }] }
// Response (example): { items: [{ ID, ITEM_NUMBER, QTY_AVAILABLE }] }
export async function checkAvailability(items) {
  const res = await fetch("/api/availability/batch", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ items }),
  });
  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    throw new Error(msg || "Failed to check live availability.");
  }
  return res.json(); // { items: [...] }
}
